import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

// ─── Accent-fold helper ───────────────────────────────────────────────────────
//
// Strips common Latin diacritics so that "blusa" and "blúsa" map to the same
// search query. Critically, ñ/Ñ must NOT be stripped — in Spanish, "año"
// (year) and "ano" (anus) are unrelated words. The vanilla NFD decomposition
// splits ñ into n + combining-tilde (U+0303), which \p{Diacritic} then
// removes, leaving plain "n". The fix: swap ñ/Ñ to Private-Use-Area
// sentinels before decomposition, then restore them afterwards.
//
// PUA sentinels chosen: U+E001 (ñ) and U+E002 (Ñ) — within the BMP PUA
// block (U+E000–U+F8FF), guaranteed never to appear in Spanish user input.
//
// Verification: accentFold('año') === 'año'  ✓
//               accentFold('blúsa') === 'blusa'  ✓
//               accentFold(accentFold(x)) === accentFold(x)  ✓ (idempotent)

const PUA_N_LOWER = ''; // sentinel for ñ
const PUA_N_UPPER = ''; // sentinel for Ñ

/**
 * Strips Latin diacritics while preserving ñ/Ñ (Spanish-critical).
 * @param {string} s
 * @returns {string}
 */
function accentFold(s) {
  // 1. Protect ñ/Ñ with PUA sentinels before NFD decomposition.
  s = s.replace(/ñ/g, PUA_N_LOWER).replace(/Ñ/g, PUA_N_UPPER);
  // 2. Decompose + strip combining diacritics from all other letters.
  s = s.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  // 3. Restore ñ/Ñ from sentinels.
  s = s.replace(//g, 'ñ').replace(//g, 'Ñ');
  return s;
}

// ─── LRU Cache (size 5) ───────────────────────────────────────────────────────
//
// Map is insertion-ordered in JS. Eviction: when adding a 6th key, delete the
// first (least-recently-used) key. On cache hit, delete + re-insert to move
// the entry to most-recently-used position.
//
// Cache key: accentFold(query).toLowerCase() — so "blúsa", "blusa", "BLÚSA"
// all share the same cache row.
//
// Cache value: merged HTML string (from #mergeAndDedupe). Stored as a string
// so the cached entry is never mutated by morph (morph works on live DOM;
// storing a string avoids stale-fragment issues).

class LruCache {
  /** @type {Map<string, string>} */
  #store = new Map();
  #maxSize;

  /** @param {number} maxSize */
  constructor(maxSize) {
    this.#maxSize = maxSize;
  }

  /** @param {string} key */
  has(key) {
    return this.#store.has(key);
  }

  /**
   * @param {string} key
   * @returns {string | undefined}
   */
  get(key) {
    if (!this.#store.has(key)) return undefined;
    // Move to most-recent position.
    const value = /** @type {string} */ (this.#store.get(key));
    this.#store.delete(key);
    this.#store.set(key, value);
    return value;
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  set(key, value) {
    if (this.#store.has(key)) {
      this.#store.delete(key);
    } else if (this.#store.size >= this.#maxSize) {
      // Evict least-recently-used (first inserted key).
      const lruKey = this.#store.keys().next().value;
      this.#store.delete(lruKey);
    }
    this.#store.set(key, value);
  }
}

// ─── Debounce delay + cache size constants (grepped by AC3 verification) ─────
const DEBOUNCE_DELAY = 200;
const CACHE_SIZE = 5;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** @type {LruCache} */
  #searchCache = new LruCache(CACHE_SIZE);

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, DEBOUNCE_DELAY);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Build the section-renderer URL for a given search query.
   * @param {string} q - The search term.
   * @returns {URL}
   */
  #buildSearchUrl(q) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[limit_scope]', 'each');
    return url;
  }

  /**
   * Extract a deduplication key for a product result card.
   * Uses the product URL path (e.g. "/products/cardigan-de-algodon") extracted
   * from the resource-card anchor href — avoids needing data-product-handle
   * attributes in the Liquid templates.
   * @param {Element} cardEl
   * @returns {string}
   */
  #getProductKey(cardEl) {
    const link = cardEl.querySelector('a.resource-card__link');
    if (link instanceof HTMLAnchorElement) {
      try {
        return new URL(link.href).pathname;
      } catch {
        return link.getAttribute('href') || '';
      }
    }
    return '';
  }

  /**
   * Merge two section HTML strings — literal-query result + stripped-query result.
   * Products from the literal query appear first; stripped-only products are
   * appended (deduped by product URL path). Non-product results (suggestions,
   * collections, pages, articles) come from the literal result unchanged.
   *
   * @param {string} literalHtml  - Full HTML string from literal-query fetch.
   * @param {string} strippedHtml - Full HTML string from stripped-query fetch.
   * @returns {string} Merged outerHTML string for the #predictive-search-results element.
   */
  #mergeAndDedupe(literalHtml, strippedHtml) {
    const parser = new DOMParser();
    const literalDoc = parser.parseFromString(literalHtml, 'text/html');
    const strippedDoc = parser.parseFromString(strippedHtml, 'text/html');

    const literalResults = literalDoc.querySelector('#predictive-search-results');
    const strippedResults = strippedDoc.querySelector('#predictive-search-results');

    if (!literalResults || !strippedResults) {
      // Fall back: return literal result as-is.
      return literalHtml;
    }

    const literalProductContainer = literalResults.querySelector(
      '.predictive-search-results__wrapper-products'
    );
    const strippedProductContainer = strippedResults.querySelector(
      '.predictive-search-results__wrapper-products'
    );

    if (!literalProductContainer || !strippedProductContainer) {
      return literalHtml;
    }

    // Collect keys already present in the literal result.
    /** @type {Set<string>} */
    const seenKeys = new Set();
    const literalCards = Array.from(
      literalProductContainer.querySelectorAll('.predictive-search-results__card--product')
    );
    for (const card of literalCards) {
      const key = this.#getProductKey(card);
      if (key) seenKeys.add(key);
    }

    // Append stripped-only product cards (those not in literal results).
    const strippedCards = Array.from(
      strippedProductContainer.querySelectorAll('.predictive-search-results__card--product')
    );
    for (const card of strippedCards) {
      const key = this.#getProductKey(card);
      if (key && !seenKeys.has(key)) {
        seenKeys.add(key);
        literalProductContainer.appendChild(card.cloneNode(true));
      }
    }

    return literalResults.outerHTML;
  }

  /**
   * Fetch search results with accent-fold dual-fetch + LRU cache, then update
   * the results container via morph (batched into a requestAnimationFrame).
   *
   * AC2: If folded === searchTerm (no diacritics), single fetch only.
   *      Else dual-fetch via Promise.all (literal + stripped).
   * AC3: Debounce is already handled by `search` (200 ms).
   *      LRU cache key: folded.toLowerCase() — blúsa/blusa/BLÚSA same row.
   * AC4 (INP): DOM updates batched via requestAnimationFrame; input field
   *      stays responsive during async fetch.
   *
   * @param {string} searchTerm - The term to search for.
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const folded = accentFold(searchTerm);
    const cacheKey = folded.toLowerCase();

    // ── Cache hit: synchronous sub-frame return ───────────────────────────────
    if (this.#searchCache.has(cacheKey)) {
      const cached = /** @type {string} */ (this.#searchCache.get(cacheKey));
      const { predictiveSearchResults } = this.refs;
      requestAnimationFrame(() => {
        morph(predictiveSearchResults, cached);
        this.#resetScrollPositions();
      });
      return;
    }

    // ── Cache miss: network fetch ─────────────────────────────────────────────
    const abortController = this.#createAbortController();
    const { predictiveSearchResults } = this.refs;

    try {
      let resultsMarkup;

      if (folded === searchTerm) {
        // No diacritics — single fetch (AC2: no wasted second request).
        resultsMarkup = await sectionRenderer.getSectionHTML(
          this.dataset.sectionId,
          false,
          this.#buildSearchUrl(searchTerm)
        );
      } else {
        // Diacritics present — dual-fetch (AC2: two requests via Promise.all).
        const [literalMarkup, strippedMarkup] = await Promise.all([
          sectionRenderer.getSectionHTML(this.dataset.sectionId, false, this.#buildSearchUrl(searchTerm)),
          sectionRenderer.getSectionHTML(this.dataset.sectionId, false, this.#buildSearchUrl(folded)),
        ]);

        if (literalMarkup && strippedMarkup) {
          resultsMarkup = this.#mergeAndDedupe(literalMarkup, strippedMarkup);
        } else {
          resultsMarkup = literalMarkup || strippedMarkup;
        }
      }

      if (!resultsMarkup) return;
      if (abortController.signal.aborted) return;

      // Store merged result in LRU cache.
      this.#searchCache.set(cacheKey, resultsMarkup);

      // Batch DOM update via requestAnimationFrame (INP discipline — AC4).
      requestAnimationFrame(() => {
        if (abortController.signal.aborted) return;
        morph(predictiveSearchResults, resultsMarkup);
        this.#resetScrollPositions();
      });
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
